// [목적] GET /users/me/export 클라이언트 — 내 데이터(JSON) 다운로드용 raw fetch 래퍼
// [이유] FlowLine b609867a FE 과업2 — 이 엔드포인트는 성공(200) 응답이 envelope({data,error}) 이 아닌
//        raw JSON 첨부(Content-Disposition: attachment)라 fetchWithEnvelope(모든 응답을 {data,error}
//        로 가정)를 그대로 쓸 수 없다(folio-export.client.ts buildFolioExportDownloadUrl 이 바이너리
//        응답을 <a download> 네비게이션으로 우회한 것과 동일 이유 — 다만 이번엔 URL 네비게이션이 아니라
//        Blob 다운로드가 요구사항이라 직접 fetch 후 Blob 변환). 에러 응답(401/429 등)은 BE
//        all-exception.filter.ts 가 여전히 {data:null,error:{code,message}} 로 감싸므로 그 형태로 파싱한다.
// [사이드 이팩트] credentials:'include' 로 same-origin 쿠키(gw_at) 전송 — GET 이라 CSRF 헤더 불필요
//                (http.ts MUTATION_METHODS 규칙과 동일 원칙). 401 발생 시 http.ts 의 single-flight
//                refresh 재시도를 거치지 않음(ponytail — 다운로드 버튼 1회성 액션이라 재시도 가치가 낮음,
//                필요해지면 fetchWithEnvelope 의 refresh 로직을 별도 공용 함수로 추출해 재사용할 것).
// [수정 시 고려사항] 파일명은 응답 헤더 Content-Disposition 우선, 파싱 실패 시 오늘 날짜 기준 기본값으로 폴백.

import { ApiError, NetworkError, apiBase, refreshSessionOnce } from './http.js';

export interface UserExportResult {
  blob: Blob;
  filename: string;
}

/**
 * [목적] Content-Disposition 헤더에서 filename 추출
 * [이유] BE 가 `filename="gangwoncanvas-export-YYYYMMDD.json"` 형태로 내려줌 — 없거나 파싱 실패 시
 *        오늘 날짜 기준 동일 포맷 기본값 사용.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] BE 가 filename*=UTF-8'' 인코딩(RFC 5987)을 쓰게 되면 이 정규식으로 잡히지 않음 —
 *                   현재는 ASCII 파일명만 계약(BE 주석 근거)이라 단순 정규식으로 충분.
 */
function parseFilename(contentDisposition: string | null): string {
  const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
  if (match?.[1]) return match[1];
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `gangwoncanvas-export-${today}.json`;
}

/**
 * [목적] GET /users/me/export 호출 — 성공 시 Blob + 파일명, 실패 시 ApiError throw
 * [이유] ExportScreen "내 데이터 다운로드(JSON)" 버튼의 유일한 API 진입점.
 * [사이드 이팩트] 없음(조회 전용, 서버 상태 변경 없음) — BE RateLimitGuard 시간당 5회 초과 시 429.
 * [수정 시 고려사항] 401 은 refresh 1회 후 재시도, 그래도 실패하면 ApiError(401). 429(레이트리밋)는 ApiError.status 로 호출부가 분기.
 */
export async function exportMyData(_isRetry = false): Promise<UserExportResult> {
  const url = `${apiBase}/users/me/export`;
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', credentials: 'include' });
  } catch (err) {
    throw new NetworkError(err);
  }

  // 2026-09-17 메인 검수: access token 만료(401) 시 공용 single-flight refresh 후 1회 재시도(fetchWithEnvelope 와 동일 정책)
  if (res.status === 401 && !_isRetry) {
    await refreshSessionOnce();
    return exportMyData(true);
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // 비-JSON 에러 응답은 기본 코드/메시지 유지
    }
    throw new ApiError(res.status, code, message);
  }

  const blob = await res.blob();
  const filename = parseFilename(res.headers.get('content-disposition'));
  return { blob, filename };
}
